import { DeprecatedInventoryItem } from '@karve.it/interfaces/estimate';

import { BaseConfigValueFragment, ZoneConfigKey } from '../../generated/graphql.generated';
import { DEFAULT_HOW_HEARD_DROPDOWN_OPTIONS, DEFAULT_JOB_CLOSED_REASONS, JOB_ORIGINS, CUSTOMER_TYPES } from '../global.constants';
import { safeParseJSON } from '../js';

export interface FrontendConfig {
	key: string;
	format?: 'string' | 'boolean' | 'number' | 'object' | 'array',
	default?: any,
	includeInBranding: boolean;
}

export const frontendConfigs = [
	{
		key: 'jobs.closedReasons',
		includeInBranding: true,
		format: 'array',
		default: DEFAULT_JOB_CLOSED_REASONS,
	},
	{
		key: 'inventory.items',
		includeInBranding: true,
		format: 'array',
		default: [] as DeprecatedInventoryItem[],
	},
	{
		key: 'invoice.disableMultipleEvents',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'jobs.howHeardOptions',
		includeInBranding: true,
		format: 'array',
		default: DEFAULT_HOW_HEARD_DROPDOWN_OPTIONS as string[],
	},
	{
		key: 'jobs.origins',
		includeInBranding: true,
		format: 'array',
		default: JOB_ORIGINS as string[],
	},
	{
		key: 'customer.types',
		includeInBranding: true,
		format: 'array',
		default: CUSTOMER_TYPES as string[],
	},
	{
		key: 'calculator.url',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url',
		includeInBranding: true,
		format: 'string',
	},
	// One key per event type defined in code (`defaultEventTypeInfoMapV2`). Types that
	// exist only in zone config — book-off, otr-fees, storage, generic — have no key and
	// use `calendarEvents.types[<type>].navigatorUrl` instead.
	{
		key: 'navigator.url-moving',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-estimating',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-virtualEstimate',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-delivery',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-packing',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-unpacking',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-driving',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-load',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'navigator.url-unload',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'tags.defaultTagNames',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'jobs.accessMandatory',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'jobs.bedroomsMandatory',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'jobs.generateEstimatePreview',
		includeInBranding: true,
		format: 'boolean',
		default: true,
	},
	{
		key: 'role.customer',
		includeInBranding: true,
	},
	{
		key: 'yembo-integration.enabled',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'franchise-info.dockLocationId',
		includeInBranding: true,
	},
	{
		key: 'calendarEvents.estimateLength',
		includeInBranding: true,
		format: 'number',
	},
	{
		key: 'calendarEvents.virtualEstimateLength',
		includeInBranding: true,
		format: 'number',
	},
	{
		key: 'rolling-lock-date.currentDate',
		includeInBranding: true,
		format: 'number',
	},
	{
		key: 'find.startTimeInterval',
		includeInBranding: true,
		format: 'number',
	},
	{
		key: 'calendarEvents.includeDockTravelTime',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'calendarEvents.dockTravelTimeCheckBox',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'calendarEvents.defaultDeposit',
		includeInBranding: true,
		format: 'number',
	},
	{
		key: 'calendarEvents.depositRequiredToBook',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'pricingEngine.enabled',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'ringcentral.enabled',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'dialpad.enabled',
		includeInBranding: true,
		format: 'boolean',
	},
	{
		key: 'dialpad.clientId',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'jobs.autoAssignAgentExceptionEmails',
		includeInBranding: true,
		format: 'string',
	},
	{
		key: 'jobs.agentsCanReassignSalesAgent',
		includeInBranding: true,
		format: 'boolean',
	},
] as const;

export const configKeys = frontendConfigs.map((c) => c.key);
export type ConfigKey = typeof frontendConfigs[number]['key'];

// Helper type to get array item type from 'default' if it is an array
type DefaultArrayType<T> = T extends Array<infer U> ? U[] : never;

// Utility type to handle types based on 'format' and 'default'
type ConfigTypes<K extends ConfigKey> =
  Extract<typeof frontendConfigs[number], { key: K }> extends { format: 'boolean' }
    ? boolean
    : Extract<typeof frontendConfigs[number], { key: K }> extends { format: 'number' }
    ? number
    : Extract<typeof frontendConfigs[number], { key: K }> extends { format: 'object' }
    ? object
    : Extract<typeof frontendConfigs[number], { key: K }> extends { format: 'array', default: infer D }
    ? DefaultArrayType<D>
    : Extract<typeof frontendConfigs[number], { key: K }> extends { format: 'string' }
    ? string
    : any;

export type ConfigValue<K extends ConfigKey> = ConfigTypes<K>;

// export const brandingConfigKeys = frontendConfigs.filter((f) => f.includeInBranding).map((c) => c.key)
export const brandingConfigKeys = [
    ZoneConfigKey.JobsClosedreasons,
    ZoneConfigKey.InventoryItems,
    ZoneConfigKey.InvoiceDisablemultipleevents,
    ZoneConfigKey.JobsHowheardoptions,
	ZoneConfigKey.JobsOrigins,
    ZoneConfigKey.JobsDefaultcreatedatfilter,
	ZoneConfigKey.CustomerTypes,
	ZoneConfigKey.JobsHowheardmandatory,
	ZoneConfigKey.JobsDwellingtypemandatory,
	ZoneConfigKey.JobsAccessmandatory,
	ZoneConfigKey.JobsBedroomsmandatory,
	ZoneConfigKey.JobsGenerateestimatepreview,
	ZoneConfigKey.RoleCustomer,
    ZoneConfigKey.YemboIntegrationEnabled,
    ZoneConfigKey.QubeSheetsIntegrationEnabled,
    ZoneConfigKey.FranchiseInfoDocklocationid,
    ZoneConfigKey.CalendareventsEstimatelength,
    ZoneConfigKey.CalendareventsVirtualestimatelength,
    ZoneConfigKey.RollingLockDateCurrentdate,
    ZoneConfigKey.FindStarttimeinterval,
    ZoneConfigKey.CalendareventsIncludedocktraveltime,
    ZoneConfigKey.CalendareventsDocktraveltimecheckbox,
    ZoneConfigKey.CalendareventsDefaultdeposit,
    ZoneConfigKey.CalendareventsDepositrequiredtobook,
    ZoneConfigKey.PricingengineEnabled,
	ZoneConfigKey.CalendareventsTypes,
	ZoneConfigKey.CalendareventsAutosaveeventtimer,
	ZoneConfigKey.GooglemapsPlacesapikey,
	ZoneConfigKey.CalculatorUrl,
	ZoneConfigKey.NavigatorUrl,
	ZoneConfigKey.NavigatorUrlMoving,
	ZoneConfigKey.NavigatorUrlEstimating,
	ZoneConfigKey.NavigatorUrlVirtualestimate,
	ZoneConfigKey.NavigatorUrlDelivery,
	ZoneConfigKey.NavigatorUrlPacking,
	ZoneConfigKey.NavigatorUrlUnpacking,
	ZoneConfigKey.NavigatorUrlDriving,
	ZoneConfigKey.NavigatorUrlLoad,
	ZoneConfigKey.NavigatorUrlUnload,
	ZoneConfigKey.TagsDefaulttagnames,
	ZoneConfigKey.RingcentralEnabled,
	ZoneConfigKey.DialpadEnabled,
    ZoneConfigKey.DialpadClientid,
    ZoneConfigKey.JobsAutoassignagentexceptionemails,
    ZoneConfigKey.JobsAgentscanreassignsalesagent,
    // Requirement: the job page hides Take Payment where no payment processor is
    // enabled and configured. See branding.graphql for why it is these two keys
    // and not the credentials.
    ZoneConfigKey.PaymentTypeBluesagePaylink,
    ZoneConfigKey.BluesageLocationid,
    ZoneConfigKey.StandardDocumentsReceipt,
	ZoneConfigKey.FranchiseInfoPhone,
	ZoneConfigKey.LegalTaxnumbers,
	ZoneConfigKey.LegalLegalcompanyname,
	ZoneConfigKey.LegalOperatingas,
]

export function formatConfigValue(config: BaseConfigValueFragment): string | boolean | number | object | any[]{
	const frontendConfig: FrontendConfig = frontendConfigs.find((c) => c.key === config.key);
	if (!frontendConfig) { return config.value; }


	if (frontendConfig.format === 'boolean' && !config.value) {
		return frontendConfig.default || false;
	}

	if (frontendConfig.format === 'boolean') {
		return config.value?.toLowerCase() === 'true';
	}

	if (frontendConfig.format === 'number') {
		return parseFloat(config.value) || frontendConfig.default;
	}

	if (frontendConfig.format === 'array') {
		return safeParseJSON(config.value, frontendConfig.default);
	}

	if (frontendConfig.format === 'object') {
		if (!config.value) { return frontendConfig.default; }
		try {
			return JSON.parse(config.value);
		} catch (err) {
			console.error(`Failed to parse ${ config.key }, reverting to default`,
				err, config.value, frontendConfig.default
			);
			return frontendConfig.default;
		}
	}

	return config.value || frontendConfig.default;
}
